import { PageSize } from '@/constant';
import {
	SearchParamsType,
	searchParamsTypeKeys,
	useMergingState,
	useSearchFilterParams,
	useSearchFilterSubmit,
} from '@/hooks';
import { GetListPageParams } from '@/services';
import { RootState } from '@/stores';
import {
	convertCamelToSnakeCaseWithoutTableName,
	deepCompare,
	formatDate,
	formatSearchValue,
	insertObjectIfElse,
	insertObjectIfV2,
	toCamelCase,
} from '@/utils';
import {
	ForwardedRef,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { DefaultOptionType } from 'antd/es/select';

import { FormikValues, useFormik } from 'formik';
import {
	clone,
	includes,
	indexBy,
	isNotEmpty,
	keys,
	length,
	map,
	omit,
	pick,
	prop,
	values,
} from 'ramda';
import {
	isArray,
	isBoolean,
	isFalse,
	isNilOrEmpty,
	isNonEmptyArray,
	isNotNilOrEmpty,
	isNull,
} from 'ramda-adjunct';

import {
	getDefaultValue,
	initialValues as getInitialValues,
	startEndDateTimeFields,
	TriggerClearSearchButton,
	typeFields,
} from './config';
import {
	EEditableFilterType,
	ISearchFilter,
	ISearchFilterRef,
	TOptions,
	TRootOptions,
	TSearchRequest,
	TSelectDateValue,
	TSelectTimeValue,
	TState,
} from './type';

const getPropsBySearchParams = ({
	searchParams,
	optionsInit,
	paginationInit,
}: {
	searchParams: SearchParamsType;
	optionsInit: TOptions;
	paginationInit?: GetListPageParams;
}) => {
	const formikValuesParams = omit(searchParamsTypeKeys, searchParams);

	const optionsSearchParams = pick(['options'], searchParams);

	let optionsState = {} as typeof optionsInit;

	if (isNonEmptyArray(optionsSearchParams?.options)) {
		const newOptionsFromParams = optionsSearchParams.options.filter(
			(optionValue: string) =>
				!optionsInit?.defaultOptions?.some(
					(defaultOp) => defaultOp.value === optionValue
				)
		);

		const remainingRootOptions =
			optionsInit?.rootOptions?.filter(
				(op) => !newOptionsFromParams.includes(op.value)
			) || [];

		const newOptions =
			optionsInit?.rootOptions?.filter((op) =>
				newOptionsFromParams.includes(op.value)
			) || [];

		optionsState = {
			...optionsInit,
			rootOptions: remainingRootOptions,
			options: newOptions,
		};
	}

	const optionStateParams = {
		...insertObjectIfElse(isNotEmpty(optionsState), optionsState, optionsInit),
	} as TState;

	const paginationSearchParams = pick(['page', 'pageSize'], searchParams);

	const paginationParams = {
		...insertObjectIfElse(
			isNotNilOrEmpty(paginationSearchParams),
			paginationSearchParams,
			paginationInit
		),
	} as GetListPageParams;

	return {
		formikValuesParams,
		optionStateParams,
		paginationParams,
	};
};

const useLogic = (
	props: ISearchFilter,
	ref: ForwardedRef<ISearchFilterRef>
) => {
	const {
		searchFilterType,
		rootOptions,
		initialParams,
		tableType,
		ignoreFirstSearch,
		onClickResetCallback,
	} = props;

	const { t } = useTranslation('common');

	const selectParams = useSelector(
		(state: RootState) => searchFilterType && state[searchFilterType].params
	);

	const { onSubmit } = useSearchFilterSubmit({
		searchFilterType,
		pagination: { ...selectParams },
		initialParams,
	});

	const [searchParams, setSearchParams] = useSearchFilterParams(
		tableType || ''
	);

	const rootOptionsClone = useMemo(() => clone(rootOptions), [rootOptions]);

	const optionsInit = useMemo(
		() => ({
			defaultOptions: rootOptionsClone?.filter((_) => _?.static),
			rootOptions: rootOptionsClone?.filter((_) => !_?.static),
			options: [] as TRootOptions[],
		}),
		[rootOptionsClone]
	);

	const paginationInit: GetListPageParams = useMemo(
		() => ({
			page: isNilOrEmpty(searchParams?.page) ? 1 : selectParams?.page || 1,
			pageSize: selectParams?.pageSize || PageSize.XS,
		}),
		[selectParams, searchParams]
	);

	const initialValues = useMemo(
		() => getInitialValues(rootOptionsClone),
		[rootOptionsClone]
	);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues,
		onSubmit: (values) => {
			const searchParamsRequest: TSearchRequest = {
				...getSearchValue({
					...state,
					formikValues: values,
				}),
				...paginationInit,
				page: 1,
			};

			onSubmit(searchParamsRequest);
		},
	});

	const isSearching = useMemo(() => {
		return !deepCompare(initialValues, formik.values);
	}, [formik.values, initialValues]);

	const [state, setState] = useMergingState<TState>({
		...optionsInit,
		isSearch: false,
		formikValues: {} as FormikValues,
		sortSql: '',
	});

	const handleSearchParam = useCallback(
		(reset?: boolean) => {
			const urlSearchParams = {
				// ...(reset ? formik.initialValues : formik.values),
				options: map(prop('value'), state.options),
				page: 1,
			};

			setSearchParams({ ...urlSearchParams });
		},
		[formik, state.options, setSearchParams]
	);

	const onClickReset = useCallback(() => {
		formik.resetForm();

		handleSearchParam(true);

		onClickResetCallback?.();
	}, [formik, handleSearchParam, onClickResetCallback]);

	const getSearchValue = useCallback((stateProps: TState) => {
		const keysDefaultExist = keys(
			indexBy(prop('value'), stateProps?.defaultOptions)
		);
		const keysExist = keys(indexBy(prop('value'), stateProps?.options));

		const keysMerged = [...keysDefaultExist, ...keysExist];

		const optionsMerged = [...stateProps.defaultOptions, ...stateProps.options];

		// const fDefaultValues: TSearchRequest = pickBy(
		// 	(val: string, key: unknown) =>
		// 		includes(key, keysDefaultExist) && isNotNilOrEmpty(val),
		// 	formikValues
		// );

		let searchTypesProps: Omit<
			TSearchRequest,
			| 'page'
			| 'pageSize'
			| 'allData'
			| 'exportExcel'
			| 'sortSql'
			| 'downloadReason'
			| 'downloadFileName'
		> = {};

		Object.entries(pick(keysMerged, stateProps?.formikValues))
			.filter(
				([key, value]) =>
					isNotNilOrEmpty(value) ||
					isNotNilOrEmpty((value as DefaultOptionType)?.value)
			)
			.forEach(([key, value]) => {
				const searchTypePrefixKey = optionsMerged?.find(
					(op) => op?.prefixKey && includes(op?.value, key)
				)?.prefixKey;

				const searchType =
					searchTypePrefixKey && isNotNilOrEmpty(searchTypePrefixKey)
						? convertCamelToSnakeCaseWithoutTableName(
								key,
								searchTypePrefixKey,
								true
							)
						: key?.toUpperCase();

				const searchTypeKey = (
					searchTypePrefixKey
						? `${toCamelCase(searchTypePrefixKey)}SearchTypes`
						: 'searchTypes'
				) as keyof typeof searchTypesProps;

				let searchTypeValues = searchTypesProps?.[searchTypeKey] ?? [];

				if (Object.hasOwn(value, 'value')) {
					// OptionType
					searchTypeValues.push({
						searchType,
						searchValue: (value as DefaultOptionType)?.value,
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				} else if (value?.type === EEditableFilterType.DATE_RANGE_PICKER) {
					// DateType
					if (
						isNilOrEmpty(value?.startDate) &&
						isNilOrEmpty(value?.endDate) &&
						isNull(value.searchYes) &&
						isNull(value.searchNo)
					)
						return;

					const outputFormat = 'YYYY-MM-DD';
					const startDate = formatDate((value as TSelectDateValue)?.startDate, {
						outputFormat,
					});
					const endDate = formatDate((value as TSelectDateValue)?.endDate, {
						outputFormat,
					});
					searchTypeValues.push({
						searchType,
						...insertObjectIfElse(
							includes(searchType, startEndDateTimeFields),
							{
								startDateTime: startDate,
								endDateTime: endDate,
							},
							{
								startDate,
								endDate,
							}
						),
						searchBoolean: value.searchYes
							? true
							: value.searchNo
								? false
								: null,
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				} else if (value?.type === EEditableFilterType.TIME_PICKER) {
					if (isNilOrEmpty(value?.startTime) && isNilOrEmpty(value?.endTime))
						return;
					// TimeType
					searchTypeValues.push({
						searchType,
						startTime: formatDate((value as TSelectTimeValue)?.startTime, {
							outputFormat: 'HH:mm',
						}),
						endTime: formatDate((value as TSelectTimeValue)?.endTime, {
							outputFormat: 'HH:mm',
						}),
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				} else if (value?.type === EEditableFilterType.INPUT_TARGET) {
					if (isNilOrEmpty(value?.searchValue)) return;
					searchTypeValues.push({
						searchType: value.searchType,
						searchValue: value.searchValue?.trim(),
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				} else if (value?.type === EEditableFilterType.INPUT_CHECKBOX) {
					if (
						isNilOrEmpty(value?.searchValue) &&
						isNull(value.searchYes) &&
						isNull(value.searchNo)
					)
						return;
					searchTypeValues.push({
						searchType,
						searchValue: value.searchValue?.trim(),
						searchBoolean: value.searchYes
							? true
							: value.searchNo
								? false
								: null,
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				} else if (isArray(value)) {
					if (includes(searchType, typeFields)) {
						if (length(value) > 1) return;
						searchTypeValues.push({
							searchType,
							searchValue: values(value)?.toString(),
						});
						searchTypesProps = {
							...searchTypesProps,
							[searchTypeKey]: searchTypeValues,
						} as TSearchRequest;
					} else {
						searchTypeValues.push({
							searchType,
							searchValueIn: value,
						});
						searchTypesProps = {
							...searchTypesProps,
							[searchTypeKey]: searchTypeValues,
						} as TSearchRequest;
					}
				} else {
					if (isBoolean(value) && isFalse(value)) return;
					searchTypeValues.push({
						searchType,
						searchValue: formatSearchValue(value),
					});
					searchTypesProps = {
						...searchTypesProps,
						[searchTypeKey]: searchTypeValues,
					} as TSearchRequest;
				}
			});

		return {
			// ...fDefaultValues,
			...insertObjectIfV2(isNotNilOrEmpty(searchTypesProps), {
				...searchTypesProps,
			}),
			page: 1,
			sortSql: stateProps.sortSql,
		} as TSearchRequest;
	}, []);

	const handleForwardRefSearch = useCallback(
		(params: Partial<TSearchRequest>) => {
			const searchParamsRequest: TSearchRequest = {
				...getSearchValue(state),
				...params,
			};
			onSubmit(searchParamsRequest);
		},
		[state, getSearchValue, onSubmit]
	);

	const handleSetValue = useCallback(
		(values: FormikValues, options?: { includePrevValue?: boolean }) => {
			formik.setValues((prev) => ({
				...initialValues,
				...insertObjectIfV2(options?.includePrevValue, prev),
				...values,
			}));
			onSearch();
		},
		[formik, initialValues]
	);

	const onSearchChange = useCallback(
		(page?: number) => {
			handleForwardRefSearch({
				page: page || selectParams?.page || 1,
				pageSize: selectParams?.pageSize || 10,
			});
		},
		[handleForwardRefSearch, selectParams]
	);

	const onPageChange = useCallback(
		(page: number, pageSize: number) => {
			handleForwardRefSearch({
				page,
				pageSize,
			});
		},
		[handleForwardRefSearch]
	);

	const onPageSizeChange = useCallback(
		(
			value: DefaultOptionType['value'],
			option: DefaultOptionType | DefaultOptionType[]
		) => {
			handleForwardRefSearch({
				page: 1,
				pageSize: value as PageSize,
			});
		},
		[handleForwardRefSearch]
	);

	const initialSearchParams = useMemo(() => {
		const searchTypeObject: Record<string, TSearchRequest['searchTypes']> = {
			searchTypes: [],
		};

		rootOptionsClone.reduce((acc, op) => {
			if (!op?.prefixKey) return acc;

			const searchTypeKey = `${toCamelCase(op?.prefixKey)}SearchTypes`;

			if (isNilOrEmpty(acc?.[searchTypeKey])) {
				acc[searchTypeKey] = [];
			}

			return acc;
		}, searchTypeObject);

		return {
			page: 1,
			...searchTypeObject,
		};
	}, [rootOptionsClone]);

	const resetSearch = useCallback(() => {
		onClickReset?.();

		setState({
			formikValues: formik.initialValues,
		});

		handleForwardRefSearch(initialSearchParams);
	}, [formik, onClickReset, handleForwardRefSearch, initialSearchParams]);

	const handleSetFilter = useCallback(
		(keys: string[]) => {
			const optionsArray = rootOptions?.filter(
				(op) =>
					keys.includes(op.value) &&
					!optionsInit?.defaultOptions?.some(
						(defaultOp) => defaultOp.value === op.value
					) &&
					!optionsInit?.options?.some((op) => op.value === op.value)
			);

			if (!optionsArray.length) return;

			const newRootOptions = optionsInit?.rootOptions?.filter(
				(op) => !optionsArray.some((o) => o.value === op.value)
			);

			const newOptions = [...optionsInit.options, ...optionsArray];

			setState({
				rootOptions: newRootOptions,
				options: newOptions,
			});
		},
		[optionsInit, rootOptions]
	);

	useImperativeHandle(ref, () => ({
		onSearchChange,
		onPageChange,
		onPageSizeChange,
		resetSearch,
		isSearching,
		isSearch: state.isSearch,
		handleSetValue,
		handleSetFilter,
		setSorter: ({ sortSql }: { sortSql: string }) => {
			setState({ sortSql });
			handleForwardRefSearch({ sortSql });
		},
		onSearchTrigger: onSearch,
	}));

	const onSearch = useCallback(() => {
		setState((prev) => ({
			isSearch: !prev.isSearch,
		}));
	}, []);

	const onClickSearch = useCallback(() => {
		setState({ formikValues: formik.values });

		formik.submitForm();

		handleSearchParam();
	}, [handleSearchParam, formik]);

	const handleAddFilter = useCallback(
		(
			value: string,
			option: TRootOptions | DefaultOptionType | DefaultOptionType[]
		) => {
			if (isNilOrEmpty(value)) return;

			const newRootOptions = state?.rootOptions?.filter(
				(op) => op.value !== (option as TRootOptions)?.value
			);

			const newOptions = [...state.options, option as TRootOptions];

			setState({
				rootOptions: newRootOptions,
				options: newOptions,
			});
		},
		[state.rootOptions, state.options]
	);

	const handleRemoveFilter = useCallback(
		(item: TRootOptions) => {
			const newRootOptions = [...state.rootOptions, item]?.sort((a, b) =>
				a?.order && b?.order ? a?.order - b?.order : 0
			);

			const newOptions = state?.options?.filter(
				(op) => op.value !== item?.value
			);

			setState({
				rootOptions: newRootOptions,
				options: newOptions,
			});

			const key = item.value;
			const value = getDefaultValue(item?.filterItem);
			formik.setFieldValue(key, value);

			onSearch();
		},
		[state.rootOptions, state.options, formik, onSearch]
	);

	const setDataBySearchParams = useCallback(() => {
		const { optionStateParams, formikValuesParams } = getPropsBySearchParams({
			searchParams,
			optionsInit,
		});

		if (isNotEmpty(formikValuesParams)) {
			formik.setValues((prev) => ({
				...prev,
				...formikValuesParams,
			}));
		}

		setState((prev) => ({
			...prev,
			...optionStateParams,
			formikValues: {
				...prev.formikValues,
				...formikValuesParams,
			},
		}));
	}, [formik, searchParams, optionsInit]);

	useEffect(() => {
		if (ignoreFirstSearch) return;

		setDataBySearchParams();
	}, [optionsInit]);

	useEffect(() => {
		if (ignoreFirstSearch) return;

		const { optionStateParams, formikValuesParams, paginationParams } =
			getPropsBySearchParams({
				searchParams,
				optionsInit,
				paginationInit,
			});

		const searchParamsRequest: TSearchRequest = getSearchValue({
			...optionStateParams,
			formikValues: {
				...initialValues,
				...formikValuesParams,
			},
		});

		// Call search API
		onSubmit({ ...searchParamsRequest, ...paginationParams });
	}, []);

	const ignoreFirstChange = useRef(false);
	useEffect(() => {
		if (!ignoreFirstChange.current) {
			ignoreFirstChange.current = true;
			return;
		}

		onClickSearch();
	}, [state.isSearch]);

	useEffect(() => {
		TriggerClearSearchButton.click = resetSearch;
	}, [resetSearch]);

	return {
		t,
		formik,
		state,
		handleAddFilter,
		handleRemoveFilter,
		onSearch,
		onClickReset,
	};
};

export default useLogic;
